/**
 * The four designed states over a query — G2 §2.6, promoted out of five feature
 * directories.
 *
 *   "Empty, loading, error and no-permission states designed for every surface — not left
 *    to the framework default."
 *
 * ── Why this is here and not in a chapter ────────────────────────────────────────────
 *
 * `AsyncRegion` and `ProblemNotice` were written independently, and near-identically, in
 * `foundations/`, `preseason/`, `launch/`, `inseason/`, `postseason/` and `exit/`. Every
 * one of those copies carries a comment saying it is a copy and asking for the promotion,
 * because `no-restricted-imports` makes a cross-feature import a build failure precisely
 * so that copying is not the easy alternative to promoting (G11: *"shared primitives live
 * in one place and are imported, not copied"*). CLAUDE.md §2.2 makes two chapters wanting
 * a component the trigger; six is not an argument, it is a backlog. `docs/adr/0012` is the
 * precedent and `docs/adr/0015` is this one's record.
 *
 * ── Why the copies existed at all ───────────────────────────────────────────────────
 *
 * Not laziness, and this matters for whether the promotion is worth anything.
 * `EmptyStateProps` took a key and no values, so the designed error surface could not
 * state a failure carrying `{detail}` or `{status}` — which is every unknown problem code
 * and every malformed response. Four of the six copies say so in their own docstrings. So
 * the primitive was widened first (`EmptyStateProps.values`), and `ProblemNotice` below is
 * built ON the designed surface rather than beside it. Promoting without that widening
 * would have shipped a shared component nobody could use.
 *
 * ── Why `src/planning/` and not `src/primitives/` ───────────────────────────────────
 *
 * `src/primitives/` is design/09 §8's inventory: presentational, and none of it knows what
 * an API is. These two know what an RFC 9457 problem document is and what a query result
 * looks like. That is planning-layer knowledge, and putting it in `primitives/` would put
 * a transport dependency underneath every button in the product.
 *
 * ── The union, not the intersection ─────────────────────────────────────────────────
 *
 * The six copies differed in exactly one behaviour: three routed a 401/403 to the
 * no-permission surface and three did not. The permission branch is kept, because G2 §2.6
 * names no-permission as its own designed state and because telling a planner that
 * something broke when nothing broke is a worse default than the alternative. The three
 * chapters that lacked it gain it.
 */

import type { ReactNode } from 'react';

import { isNotBuiltProblem, isPermissionProblem, problemMessage } from '../api';
import { EmptyState, Skeleton } from '../primitives';
import { usePageStatusEntry, type PageStatusEntry } from './page-status';
import { Panel } from './panel';

// ── a failure, stated through the catalogue ──────────────────────────────────

export interface ProblemNoticeProps {
  /** The thrown value. `unknown`, because a caller has no business narrowing it first. */
  readonly error: unknown;
  /** ICU key for the heading. Each surface names what, specifically, did not happen. */
  readonly titleKey: string;
  /**
   * Read again — offered as the page strip's «Riprova» when the page draws one strip
   * (`docs/adr/9033`). The inline notice offers no retry, as before.
   */
  readonly onRetry?: () => unknown;
}

/**
 * The state this notice states, for the page's one strip (`docs/adr/9033`): the same three
 * branches as the notice below, in the same order.
 */
export function problemStatusEntry(error: unknown, titleKey: string, onRetry?: () => unknown): PageStatusEntry {
  if (isPermissionProblem(error)) {
    return { kind: 'no-permission', message: { key: 'state.noPermission.title' }, detail: { key: 'state.noPermission.body' } };
  }
  if (isNotBuiltProblem(error)) {
    const message = problemMessage(error);
    return { kind: 'not-published', message: { key: 'state.notBuilt.title' }, detail: { key: message.key, values: message.values } };
  }
  return { kind: 'error', message: { key: titleKey }, error, ...(onRetry === undefined ? {} : { retry: onRetry }) };
}

/**
 * A failure, stated.
 *
 * `bata-platform` answers every failure with an RFC 9457 problem document whose `title`
 * and `detail` are already localised — but from the PLATFORM's catalogue, on a different
 * release cadence, written for an integrator rather than for the planner looking at this
 * screen. So `problemMessage` maps the code into the `api` namespace and only an unknown
 * code lets the service's own detail through, and then as an ICU ARGUMENT inside a
 * translated sentence rather than as the sentence. G6 test b forbids the alternative, and
 * `EmptyStateProps.values` is what makes the permitted version expressible.
 *
 * A 401 or 403 is not an error. It is the fourth designed state, and it is routed to its
 * own surface: "you may not see this" and "this did not load" are different facts and only
 * one of them is worth retrying.
 */
export function ProblemNotice({ error, titleKey, onRetry }: ProblemNoticeProps): React.JSX.Element | null {
  // Inside a page that draws one strip, the failure is said there, once, and nothing is drawn
  // here; the panel keeps whatever it had already read (ADR 9033). Outside one, as ever.
  const registered = usePageStatusEntry(problemStatusEntry(error, titleKey, onRetry));
  if (registered) return null;
  // No `placement`: `EmptyState` reads its surroundings itself — inline inside a panel,
  // standalone on the page (`primitives/enclosure.tsx`).
  if (isPermissionProblem(error)) {
    return (
      <EmptyState
        tourAnchor="page-status"
        variant="no-permission"
        titleKey="state.noPermission.title"
        bodyKey="state.noPermission.body"
      />
    );
  }
  const message = problemMessage(error);
  // The fifth designed state (ADR 0015, amended for audit v4 G-04). The service refused on
  // purpose and said why; the "could not be loaded" heading and the alert icon read it as a
  // fault to report. The caller's `titleKey` names what FAILED to load, which is not what
  // happened here, so the heading is the state's own.
  if (isNotBuiltProblem(error)) {
    return (
      <EmptyState
        tourAnchor="page-status"
        variant="not-built"
        titleKey="state.notBuilt.title"
        bodyKey={message.key}
        values={message.values}
      />
    );
  }
  return (
    <EmptyState
      tourAnchor="page-status"
      variant="error"
      titleKey={titleKey}
      bodyKey={message.key}
      values={message.values}
    />
  );
}

// ── loading, failed, empty, loaded ───────────────────────────────────────────

export interface AsyncRegionProps<T> {
  /**
   * The query result. Typed STRUCTURALLY rather than as `UseQueryResult`, so a test can
   * pass a plain object instead of mounting a `QueryClientProvider` around every
   * assertion — which is how all six chapter copies typed it, and they were right.
   */
  readonly query: {
    readonly isPending: boolean;
    readonly isError: boolean;
    readonly error: unknown;
    readonly data: T | undefined;
    /**
     * TanStack's `fetchStatus`, when the query has one. A DISABLED query is `isPending` and `idle`:
     * nothing is being read, so the skeleton must not say it is busy (C7).
     */
    readonly fetchStatus?: 'fetching' | 'paused' | 'idle';
    /** TanStack's `refetch`, when the query has one: the page strip's «Riprova» (ADR 9033). */
    readonly refetch?: () => unknown;
  };
  /** ICU key for the error heading — what, specifically, could not be loaded. */
  readonly errorTitleKey: string;
  readonly emptyTitleKey: string;
  readonly emptyBodyKey: string;
  /** ICU arguments for the empty state's title and body. */
  readonly emptyValues?: Readonly<Record<string, string | number>>;
  /** Rows of skeleton while loading. Sized to the surface, not to a default. */
  readonly loadingRows?: number;
  /**
   * Whether loaded data is EMPTY. Supplied by the caller because only the caller knows
   * what empty means for its shape — zero rows, a null total, an absent verdict.
   */
  readonly isEmpty?: (data: T) => boolean;
  /**
   * For a region that WRAPS panels rather than sitting inside one: the title of the panel
   * its loading, failed and empty states are drawn in.
   *
   * Loaded, such a region renders its own panels and this is unused. Not loaded, it used to
   * render its state bare between the panels around it — audit v4 G-31 found "NO COMPARISON
   * STORED" as a page-level block under the shadow panel, and G-08 three of them stacked
   * above the one interactive panel on consolidation, 550px of display-face headings about
   * three different reads with nothing saying which read each was about. Name the panel the
   * read would have filled, and the absence sits where the reading would have been, under
   * the heading that says what it is, in the compact inline form a panel's contents take.
   */
  readonly enclosingTitleKey?: string;
  readonly children: (data: T) => ReactNode;
}

export function AsyncRegion<T>({
  query, errorTitleKey, emptyTitleKey, emptyBodyKey, emptyValues,
  loadingRows = 4, isEmpty, enclosingTitleKey, children,
}: AsyncRegionProps<T>): React.JSX.Element {
  const enclose = (state: React.JSX.Element): React.JSX.Element => (enclosingTitleKey === undefined
    ? state
    : <Panel titleKey={enclosingTitleKey}>{state}</Panel>);

  if (query.isPending) {
    return enclose(<Skeleton rows={loadingRows} {...(query.fetchStatus === undefined ? {} : { busy: query.fetchStatus !== 'idle' })} />);
  }
  if (query.isError || query.data === undefined) {
    return enclose(
      <ProblemNotice
        error={query.error}
        titleKey={errorTitleKey}
        {...(query.refetch === undefined ? {} : { onRetry: query.refetch })}
      />,
    );
  }
  // Empty is a DESIGNED state and not "zero rows in a table": a queue with nothing in it
  // is good news and says so, and it is not the same screen as a load that failed.
  if (isEmpty?.(query.data) === true) {
    return enclose(
      <EmptyState
        titleKey={emptyTitleKey}
        bodyKey={emptyBodyKey}
        {...(emptyValues === undefined ? {} : { values: emptyValues })}
      />,
    );
  }
  return <>{children(query.data)}</>;
}
